import { useCustomCss } from '../lib/customCss';
import { useSiteTheme } from '../lib/palette';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { forgetReferral, referralCode, rememberReferral, setReferral } from '../lib/referral';
import { offerText, PartnerBanner, usePartnerOffer } from '../ui/PartnerOffer';
import { api, tokens } from '../lib/api';
import type { Parent } from '../lib/types';
import { Logo } from '../ui/Logo';
import { PasskeySignIn } from './Passkeys';

interface AuthOptions {
  registrationOpen: boolean;
  /** Passkey sign-in is on (#417). */
  passkeys?: boolean;
  passwordReset: boolean;
}

/** What this server offers on the sign-in pages (sign-ups open, password reset by email, passkeys). */
export function useAuthOptions() {
  const [options, setOptions] = useState<AuthOptions>({ registrationOpen: true, passwordReset: false });
  useEffect(() => {
    api.get<AuthOptions>('/api/auth/options').then(setOptions).catch(() => undefined);
  }, []);
  return options;
}

/**
 * Clickwrap (#589): two boxes a parent must tick, never ticked for them. The server records the Terms version, the time
 * and the IP address. Used at sign-up, when finishing an invited account, and on "We've updated our Terms".
 */
export function TermsAgreement({
  agreed,
  guardian,
  onAgreed,
  onGuardian,
}: {
  agreed: boolean;
  guardian: boolean;
  onAgreed: (v: boolean) => void;
  onGuardian: (v: boolean) => void;
}) {
  return (
    <div className="terms-agreement">
      <label className="check">
        <input type="checkbox" checked={agreed} onChange={(e) => onAgreed(e.target.checked)} required />
        <span>
          I agree to the{' '}
          <a href="/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={guardian} onChange={(e) => onGuardian(e.target.checked)} required />
        <span>I’m the parent or legal guardian of any child I add.</span>
      </label>
    </div>
  );
}

/** When sign-ups are closed, let a visitor ask an admin for an account. */
function RequestAccess() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<null | { hasAccount: boolean }>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api.post<{ ok: boolean; hasAccount?: boolean }>('/api/auth/request-access', { name, email, note: note.trim() || undefined });
      setDone({ hasAccount: !!r.hasAccount });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return done.hasAccount ? (
      <p className="muted">
        You already have an account with that email. <Link to="/login">Sign in</Link> instead.
      </p>
    ) : (
      <p className="good-text">Thanks — your request was sent. An administrator will review it and email you if it’s approved.</p>
    );
  }

  return (
    <>
      <p className="muted">
        This ClassGuardian server isn’t taking open sign-ups. Ask its administrator for an account and they’ll email you an invite if
        it’s approved.
      </p>
      <form onSubmit={submit} className="stack">
        <label>
          Your name
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required maxLength={60} />
        </label>
        <label>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
        </label>
        <label>
          Anything to tell the admin? <span className="muted small">(optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3} placeholder="e.g. I'm Sam's other parent." />
        </label>
        {error && <p className="error">{error}</p>}
        <button className="btn primary big" disabled={busy || !name.trim() || !email.includes('@')}>
          {busy ? 'Sending…' : 'Request access'}
        </button>
      </form>
    </>
  );
}

export function AuthPage({ mode, onSignedIn }: { mode: 'login' | 'signup'; onSignedIn: (p: Parent) => void }) {
  useCustomCss('login'); // an admin's custom styles (#367)
  useSiteTheme(); // the same look as the home page (#719)
  // A friend's link (#554) or a partner's (#598) straight to sign-up, and the code box that can change it.
  const [code, setCode] = useState(() => (rememberReferral(), referralCode()));
  // The partner's code from the link they came in on (#879): kept when they type a sign-up code, for commission.
  const [linkCode] = useState(() => referralCode());
  const offer = usePartnerOffer(code);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [guardian, setGuardian] = useState(false);
  const options = useAuthOptions();
  const signup = mode === 'signup';
  // Signing up from a family invite (#507): the email is the invited one, and you join that family (no family of your
  // own). Works even when new sign-ups are closed.
  const [params] = useSearchParams();
  const inviteToken = signup ? (params.get('invite') ?? '') : '';
  const [invite, setInvite] = useState<{ familyName: string; email: string } | null>(null);
  useEffect(() => {
    if (!inviteToken) return;
    api
      .get<{ familyName: string; email: string }>(`/api/family/invite/${encodeURIComponent(inviteToken)}`)
      .then((r) => {
        setInvite(r);
        setEmail(r.email);
      })
      .catch((e) => setError((e as Error).message));
  }, [inviteToken]);
  // A partner's code opens sign-up when the admin lets partners bring sign-ups (#598).
  const closed = signup && !options.registrationOpen && !inviteToken && !offer?.signupsOpen;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = signup
        ? await api.post<{ token: string; parent: Parent }>('/api/auth/signup', { email, name, password, timezone, acceptTerms: agreed, guardian, ...(inviteToken ? { inviteToken } : code.trim() ? { ref: code.trim(), ...(linkCode && linkCode !== code.trim() ? { link: linkCode } : {}) } : {}) })
        : await api.post<{ token?: string; parent?: Parent; mfaRequired?: boolean; challenge?: string }>('/api/auth/login', { email, password });
      if ('mfaRequired' in res && res.mfaRequired) {
        setChallenge(res.challenge!);
        return;
      }
      tokens.setParent(res.token!);
      if (signup) forgetReferral();
      onSignedIn(res.parent as Parent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{ token: string; parent: Parent }>('/api/auth/mfa', { challenge, code: mfaCode.trim() });
      tokens.setParent(res.token);
      onSignedIn(res.parent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <main className="auth">
        <div className="auth-card card">
          <Logo large />
          <h1>Two-factor</h1>
          <p className="muted">Enter the 6-digit code from your authenticator app, or a recovery code.</p>
          <form onSubmit={submitMfa} className="stack">
            <label>
              Code
              <input value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} autoComplete="one-time-code" inputMode="text" autoFocus required />
            </label>
            {error && <p className="error">{error}</p>}
            <button className="btn primary big" disabled={busy || !mfaCode.trim()}>
              {busy ? 'Checking…' : 'Verify'}
            </button>
          </form>
          <p className="muted center">
            <button type="button" className="link-btn" onClick={() => { setChallenge(''); setMfaCode(''); setError(''); }}>
              Back
            </button>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="auth">
      <div className="auth-card card">
        {signup && !inviteToken && <PartnerBanner offer={offer} />}
        <Link to="/" className="logo-link" aria-label="ClassGuardian home">
          <Logo large />
        </Link>
        <h1>{closed ? 'Request access' : invite ? `Join ${invite.familyName}` : signup ? 'Create your family account' : 'Welcome back'}</h1>
        {closed ? (
          <RequestAccess />
        ) : (
          <>
            <p className="muted">
              {invite
                ? 'Create your account to join. You’ll see the children they share with you; you don’t need a family of your own.'
                : signup
                ? 'Keep school time focused and safe, with a banner on their computer whenever the camera or microphone is on.'
                : 'Sign in to see how school time is going.'}
            </p>
            <form onSubmit={submit} className="stack">
              {signup && (
                <label>
                  Your name
                  <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required maxLength={60} />
                </label>
              )}
              <label>
                Email
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required readOnly={!!invite} />
              </label>
              <label>
                Password
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={signup ? 'new-password' : 'current-password'}
                  minLength={signup ? 8 : undefined}
                  required
                />
              </label>
              {signup && !inviteToken && (
                <label>
                  Have a code? <span className="muted small">(optional)</span>
                  <input
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      setReferral(e.target.value);
                    }}
                    autoCapitalize="characters"
                    autoComplete="off"
                    maxLength={16}
                  />
                  {offer && offerText(offer) && (
                    <span className="good-text small">
                      ✓ {offerText(offer)}
                      {offer.name ? `, from ${offer.name}` : ''}
                    </span>
                  )}
                </label>
              )}
              {signup && <TermsAgreement agreed={agreed} guardian={guardian} onAgreed={setAgreed} onGuardian={setGuardian} />}
              {error && <p className="error">{error}</p>}
              <button className="btn primary big" disabled={busy || (signup && (!agreed || !guardian))}>
                {busy ? 'One moment…' : signup ? 'Create account' : 'Sign in'}
              </button>
              {signup && (!agreed || !guardian) && <p className="muted small center">Tick both boxes above to create your account.</p>}
            </form>
            {!signup && options.passkeys && (
              <div className="stack auth-alt">
                <div className="or-divider muted small">or</div>
                <PasskeySignIn onSignedIn={onSignedIn} onError={setError} />
              </div>
            )}
          </>
        )}
        {!signup && options.passwordReset && (
          <p className="muted center">
            <Link to="/forgot">Forgot your password?</Link>
          </p>
        )}
        <p className="muted center">
          {signup ? (
            <>
              Already have an account? <Link to="/login">Sign in</Link>
            </>
          ) : options.registrationOpen ? (
            <>
              New here? <Link to="/signup">Create an account</Link>
            </>
          ) : (
            <>
              Need an account? <Link to="/signup">Request access</Link>
            </>
          )}
        </p>
        <p className="muted center small">
          Setting up your child’s computer? <Link to="/agent">Open the kid’s app</Link>
        </p>
      </div>
    </main>
  );
}

/** Ask for a reset link by email. Says the same thing whether or not the account exists. */
export function ForgotPassword() {
  useCustomCss('login'); // an admin's custom styles (#367)
  useSiteTheme();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/api/auth/forgot', { email });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <div className="auth-card card">
        <Logo large />
        <h1>Reset your password</h1>
        {sent ? (
          <p className="muted">If {email} has a ClassGuardian account, we’ve emailed it a link to choose a new password. It works for 2 hours.</p>
        ) : (
          <form onSubmit={submit} className="stack">
            <p className="muted">Enter your account’s email and we’ll send you a link.</p>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
            </label>
            {error && <p className="error">{error}</p>}
            <button className="btn primary big" disabled={busy}>
              {busy ? 'One moment…' : 'Email me a link'}
            </button>
          </form>
        )}
        <p className="muted center">
          <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </main>
  );
}

interface LinkInfo {
  email: string;
  name: string;
  purpose: 'reset' | 'invite';
  /** Finishing an invited account and they haven't accepted the current Terms (#589). */
  termsNeeded?: boolean;
}

/** Choose a password from an invite or reset link (`/set-password?token=…`), then sign in. */
export function SetPassword({ onSignedIn }: { onSignedIn: (p: Parent) => void }) {
  useCustomCss('login'); // an admin's custom styles (#367)
  useSiteTheme();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [invalid, setInvalid] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [guardian, setGuardian] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<LinkInfo>(`/api/auth/link/${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch((e) => setInvalid(e.message));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== again) return setError('The two passwords don’t match.');
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{ token: string; parent: Parent }>('/api/auth/link', { token, password, acceptTerms: agreed, guardian });
      tokens.setParent(res.token);
      onSignedIn(res.parent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <div className="auth-card card">
        <Logo large />
        <h1>{info?.purpose === 'invite' ? `Welcome, ${info.name}` : 'Choose a new password'}</h1>
        {invalid ? (
          <div className="stack">
            <p className="error">{invalid}</p>
            {/* An expired or used link (#739): one click to the form that emails a new one. */}
            <Link className="btn primary" to="/forgot">
              Get a new link
            </Link>
          </div>
        ) : !info ? (
          <p className="muted">One moment…</p>
        ) : (
          <form onSubmit={submit} className="stack">
            <p className="muted">
              {info.purpose === 'invite' ? 'Choose a password to finish setting up your account' : 'Choose a new password'} for{' '}
              <strong>{info.email}</strong>.
            </p>
            <label>
              New password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={8} required />
            </label>
            <label>
              Type it again
              <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" minLength={8} required />
            </label>
            {info.termsNeeded && <TermsAgreement agreed={agreed} guardian={guardian} onAgreed={setAgreed} onGuardian={setGuardian} />}
            {error && <p className="error">{error}</p>}
            <button className="btn primary big" disabled={busy || (!!info.termsNeeded && (!agreed || !guardian))}>
              {busy ? 'One moment…' : 'Save and sign in'}
            </button>
          </form>
        )}
        <p className="muted center">
          <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </main>
  );
}
